/**
 * Nolana theme — global helpers.
 * Vanilla JS, no dependencies. Ported from tema-demo/assets/js/nolana.js.
 *
 * Exposes window.Nolana with:
 *   $(selector, root) / $$(selector, root)  querySelector helpers
 *   formatMoney(cents)                      shop money format (from theme.liquid)
 *   toast(message)                          global feedback pill (#NolanaToast)
 *   reduceMotion                            prefers-reduced-motion flag
 *   on(name, handler) / emit(name, detail)  tiny event bus on document
 *   trapFocus(container, initial) / releaseFocus()   delegates to Dawn when available
 *
 * Also wires: header shadow on scroll (.header.is-scrolled) and scroll reveal (.reveal.is-in).
 */
(function () {
  "use strict";

  var config = window.NolanaConfig || {};
  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function $(selector, root) {
    return (root || document).querySelector(selector);
  }

  function $$(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  /**
   * Formats an amount in cents using the shop's money format
   * (e.g. "{{amount_with_comma_separator}} TL" -> "1.234,56 TL").
   */
  function formatMoney(cents, format) {
    if (typeof cents === "string") cents = cents.replace(".", "");
    var value = "";
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || config.moneyFormat || "{{amount_with_comma_separator}} TL";

    function formatWithDelimiters(number, precision, thousands, decimal) {
      if (isNaN(number) || number == null) return "0";
      var fixed = (number / 100).toFixed(precision);
      var parts = fixed.split(".");
      var integer = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
      var fraction = parts[1] ? decimal + parts[1] : "";
      return integer + fraction;
    }

    var match = formatString.match(placeholderRegex);
    var placeholder = match ? match[1] : "amount";
    switch (placeholder) {
      case "amount":
        value = formatWithDelimiters(cents, 2, ",", ".");
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0, ",", ".");
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount_with_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ",");
        break;
      case "amount_no_decimals_with_space_separator":
        value = formatWithDelimiters(cents, 0, " ", ",");
        break;
      default:
        value = formatWithDelimiters(cents, 2, ",", ".");
    }
    return formatString.replace(placeholderRegex, value);
  }

  /* ---------- Toast ---------- */
  var toastElement = null;
  var toastTimer = null;

  function toast(message) {
    if (!toastElement) toastElement = $("#NolanaToast");
    if (!toastElement || !message) return;
    toastElement.textContent = message;
    toastElement.classList.add("is-show");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastElement.classList.remove("is-show");
    }, 2600);
  }

  /* ---------- Event bus ---------- */
  function on(name, handler) {
    document.addEventListener("nolana:" + name, handler);
    return function () {
      document.removeEventListener("nolana:" + name, handler);
    };
  }

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent("nolana:" + name, { detail: detail || {} }));
  }

  /* ---------- Focus management (reuses Dawn's trapFocus when loaded) ---------- */
  var focusState = { container: null, handler: null, focusHandler: null, previous: null };
  var focusableSelector =
    'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, initial) {
    if (!container) return;
    if (typeof window.trapFocus === "function") {
      window.trapFocus(container, initial);
      return;
    }
    releaseFocus();
    focusState.container = container;
    focusState.previous = document.activeElement;
    focusState.handler = function (event) {
      if (event.key !== "Tab") return;
      var items = $$(focusableSelector, container).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", focusState.handler);
    // Focus that lands outside the layer (e.g. after a click on empty space) is pulled back in.
    focusState.focusHandler = function (event) {
      if (!container.contains(event.target)) {
        var inside = $(focusableSelector, container).filter(function (el) {
          return el.offsetParent !== null;
        });
        (inside[0] || container).focus();
      }
    };
    document.addEventListener("focusin", focusState.focusHandler);
    (initial || $(focusableSelector, container) || container).focus();
  }

  function releaseFocus(returnTo) {
    if (typeof window.removeTrapFocus === "function") {
      window.removeTrapFocus(returnTo);
    }
    if (focusState.handler) document.removeEventListener("keydown", focusState.handler);
    if (focusState.focusHandler) document.removeEventListener("focusin", focusState.focusHandler);
    var target = returnTo || focusState.previous;
    focusState = { container: null, handler: null, focusHandler: null, previous: null };
    if (target && typeof target.focus === "function") target.focus();
  }

  /* ---------- Header shadow ---------- */
  function initHeaderShadow() {
    var header = $(".header");
    if (!header || !("IntersectionObserver" in window)) return;
    var sentinel = document.createElement("div");
    sentinel.className = "header-sentinel";
    sentinel.style.cssText = "position:absolute;top:0;height:1px;width:1px;pointer-events:none;";
    document.body.prepend(sentinel);
    new IntersectionObserver(function (entries) {
      header.classList.toggle("is-scrolled", !entries[0].isIntersecting);
    }).observe(sentinel);
  }

  /* ---------- Scroll reveal ---------- */
  function initReveal(root) {
    var elements = $$(".reveal:not(.is-in)", root);
    if (!elements.length) return;
    if (!("IntersectionObserver" in window) || reduceMotion) {
      elements.forEach(function (el) {
        el.classList.add("is-in");
      });
      return;
    }
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-in");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    elements.forEach(function (el) {
      observer.observe(el);
    });
  }

  window.Nolana = {
    $: $,
    $$: $$,
    formatMoney: formatMoney,
    toast: toast,
    on: on,
    emit: emit,
    trapFocus: trapFocus,
    releaseFocus: releaseFocus,
    reduceMotion: reduceMotion,
    initReveal: initReveal,
    strings: config.strings || {},
  };

  function init() {
    initHeaderShadow();
    initReveal(document);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Theme Editor: re-run reveal for sections rendered after load.
  document.addEventListener("shopify:section:load", function (event) {
    initReveal(event.target);
  });
})();
